import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "260",
  "slug": "30-days-playing-earthguessr-experiment",
  "title": "A 30-Day EarthGuessr Practice Plan",
  "excerpt": "One round a day, a short guess log, and a weekly review of the places you mix up. A practice plan you can adapt to your own pace.",
  "date": "2026-05-08",
  "category": "Community",
  "image": "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80",
  "readTime": "3 min",
  "content": [
    {
      "type": "paragraph",
      "text": "Keep a notebook beside you for your next EarthGuessr round. Before guessing, write down the strongest clue you can see. After the reveal, record the actual location and whether that clue helped. A correct guess with a weak explanation is worth examining too."
    },
    {
      "type": "paragraph",
      "text": "This plan gives each week a different focus. You can spend a few minutes on the round and longer on the review when a location interests you. There is no target score or promised improvement to hit."
    },
    {
      "type": "heading",
      "text": "Week 1: Find out what you confuse"
    },
    {
      "type": "paragraph",
      "text": "Play as you normally would, but keep a log. Note your guess, the answer, and the distance between them. Add a sentence about your reasoning: “I chose Brazil because of the dense forest” is enough to start with."
    },
    {
      "type": "paragraph",
      "text": "At the end of the week, look for repeated mistakes. Perhaps you place every dry landscape in Australia, or recognise a country but miss by hundreds of kilometres. Those are different problems. Choose one to work on next week."
    },
    {
      "type": "heading",
      "text": "Week 2: Compare two plausible locations"
    },
    {
      "type": "paragraph",
      "text": "Pick a pair of regions from your log and compare them at a similar map scale. Start with the detail that confused you. If both images show forest, look beyond the trees to the rivers, clearings, and nearby terrain."
    },
    {
      "type": "paragraph",
      "text": "Write down a difference you can point to in the images. “This river crosses a broad plain; that one sits in a narrow valley” is more useful than “this feels European.” Keep the image scale in mind: a close crop may hide the feature you are looking for."
    },
    {
      "type": "heading",
      "text": "Week 3: Explain before you guess"
    },
    {
      "type": "paragraph",
      "text": "For each round, name a candidate location and an alternative. Then look for a detail that would help you choose between them. If you cannot find one, record that uncertainty instead of adding an explanation after the answer appears."
    },
    {
      "type": "paragraph",
      "text": "After the reveal, separate a missed clue from a clue that was never visible. You can practise noticing a coastline at the edge of an image. You cannot infer a coastline that falls outside the frame."
    },
    {
      "type": "heading",
      "text": "Week 4: Revisit your mistakes"
    },
    {
      "type": "paragraph",
      "text": "Return to the regions you confused in week one. Cover the labels on a map or ask someone else to select an image nearby. Try explaining the location again before checking the answer."
    },
    {
      "type": "paragraph",
      "text": "A familiar image can test whether you remember that image. A different view of the same region gives you another way to check whether the clue still helps. Keep both results in your notes."
    },
    {
      "type": "heading",
      "text": "What to keep after day 30"
    },
    {
      "type": "paragraph",
      "text": "Read through the log and choose a few entries where your reasoning changed. You might have stopped treating green vegetation as proof of a tropical climate, or started checking the direction of a mountain range before placing a guess."
    },
    {
      "type": "paragraph",
      "text": "Scores will vary with the locations you draw. Use them alongside your notes, and keep the habit that proved useful to you. That might be a daily round, an occasional map comparison, or a longer review of a place you want to understand."
    }
  ],
  "tldr": "Try one round a day for 30 days. Record your guess, the actual location, and the clue you used. Review the log each week to choose what to practise next. This is a suggested exercise, not a report of measured results."
};
